/**
 * Thư viện dựng video bán hàng (anh Tiến 26/10/2026: "chọn nhạc nền hoặc video mẫu video vui", "mẫu intro, outro, chuyển cảnh: video và âm thanh, chuẩn hot").
 * Chỉ là danh mục (mã + nhãn + gợi ý): trình duyệt tự tổng hợp nhạc / tiếng động và vẽ intro, outro, chuyển cảnh (không file bản quyền của bên thứ ba);
 * máy chủ dùng `VIDEO_TEMPLATES[].brief` để AI viết kịch bản theo mẫu. Nhạc riêng của công ty: file ghi âm gắn thẻ "nhạc nền" ở Tài liệu doanh nghiệp.
 */

export const MUSIC_MOODS = ['fun', 'energetic', 'epic', 'calm', 'promo'] as const;
export type MusicMood = (typeof MUSIC_MOODS)[number];
export const MUSIC_MOOD_LABEL: Record<MusicMood, string> = {
  fun: 'Vui',
  energetic: 'Sôi động',
  epic: 'Hùng tráng',
  calm: 'Nhẹ nhàng',
  promo: 'Tết / Khuyến mãi',
};

export const MUSIC_STYLES = [
  { id: 'ukulele', mood: 'fun', label: 'Ukulele vui tươi', bpm: 112 },
  { id: 'bounce', mood: 'fun', label: 'Nhún nhảy (quirky)', bpm: 104 },
  { id: 'whistle', mood: 'fun', label: 'Huýt sáo yêu đời', bpm: 118 },
  { id: 'edm', mood: 'energetic', label: 'EDM bùng nổ', bpm: 126 },
  { id: 'phonk', mood: 'energetic', label: 'Phonk bắt trend', bpm: 132 },
  { id: 'funk', mood: 'energetic', label: 'Funk bass dày', bpm: 116 },
  { id: 'epic', mood: 'epic', label: 'Điện ảnh hùng tráng', bpm: 90 },
  { id: 'industry', mood: 'epic', label: 'Công nghiệp mạnh mẽ', bpm: 100 },
  { id: 'lofi', mood: 'calm', label: 'Lo-fi thư giãn', bpm: 80 },
  { id: 'piano', mood: 'calm', label: 'Piano nhẹ nhàng', bpm: 76 },
  { id: 'corporate', mood: 'calm', label: 'Doanh nghiệp tích cực', bpm: 108 },
  { id: 'tet', mood: 'promo', label: 'Tết rộn ràng (ngũ cung)', bpm: 120 },
  { id: 'sale', mood: 'promo', label: 'Sale giật gân', bpm: 128 },
] as const satisfies readonly { id: string; mood: MusicMood; label: string; bpm: number }[];
export type MusicStyleId = (typeof MUSIC_STYLES)[number]['id'];
export const MUSIC_STYLE_IDS = MUSIC_STYLES.map((s) => s.id) as [MusicStyleId, ...MusicStyleId[]];

export const VIDEO_INTROS = [
  { id: 'none', label: 'Không intro' },
  { id: 'light-sweep', label: 'Logo quét sáng' },
  { id: 'bounce', label: 'Chữ nảy (pop)' },
  { id: 'paper-tear', label: 'Xé giấy' },
  { id: 'glitch', label: 'Nhiễu glitch' },
  { id: 'zoom-punch', label: 'Đập zoom' },
  { id: 'curtain', label: 'Mở rèm 2 nửa' },
  { id: 'neon', label: 'Đèn neon' },
  { id: 'countdown', label: 'Đếm ngược 3-2-1' },
  // Theo ngành (anh Tiến 26/10: "Intro: tia lửa, pccc, …")
  { id: 'sparks', label: 'Tia lửa cắt thép' },
  { id: 'fire-water', label: 'Lửa bị dập bằng nước (PCCC)' },
  { id: 'siren', label: 'Đèn báo cháy nhấp nháy' },
  { id: 'pipes', label: 'Ống thép lăn xếp thành logo' },
  { id: 'fan', label: 'Cánh quạt quay (HVAC)' },
  { id: 'blueprint', label: 'Bản vẽ kỹ thuật hiện dần' },
  // 3D (anh Tiến 26/10: "3D … chuẩn hót nhất hiện nay"): dựng phối cảnh trên canvas, không cần WebGL
  { id: '3d-logo', label: 'Logo 3D nổi khối xoay tới' },
  { id: '3d-text', label: 'Chữ 3D bay từ xa' },
  { id: '3d-cube', label: 'Khối hộp 3D xoay hiện logo' },
] as const;
export type VideoIntroId = (typeof VIDEO_INTROS)[number]['id'];
export const VIDEO_INTRO_IDS = VIDEO_INTROS.map((s) => s.id) as [VideoIntroId, ...VideoIntroId[]];

export const VIDEO_OUTROS = [
  { id: 'classic', label: 'Sáng sạch (logo + hotline)' },
  { id: 'call-now', label: 'Nút "Gọi ngay" nhấp nháy' },
  { id: 'cards', label: 'Thẻ hotline, Zalo, kho trượt vào' },
  { id: 'split', label: 'Chia đôi: logo | thông tin' },
  { id: 'neon', label: 'Nền tối neon' },
  { id: 'ticker', label: 'Băng chữ chạy' },
] as const;
export type VideoOutroId = (typeof VIDEO_OUTROS)[number]['id'];
export const VIDEO_OUTRO_IDS = VIDEO_OUTROS.map((s) => s.id) as [VideoOutroId, ...VideoOutroId[]];

export const VIDEO_TRANSITIONS = [
  { id: 'whip', label: 'Lia nhanh', sfx: 'whoosh' },
  { id: 'zoom', label: 'Zoom xuyên', sfx: 'whoosh' },
  { id: 'spin', label: 'Zoom xoáy', sfx: 'swirl' },
  { id: 'flash', label: 'Chớp trắng', sfx: 'shutter' },
  { id: 'dissolve', label: 'Hòa tan', sfx: 'soft' },
  { id: 'slide-up', label: 'Vuốt lên (lướt video)', sfx: 'swipe' },
  { id: 'glitch', label: 'Glitch', sfx: 'glitch' },
  { id: 'shake', label: 'Rung mạnh theo nhịp', sfx: 'impact' },
  { id: 'iris', label: 'Vòng tròn mở', sfx: 'pop' },
  { id: 'curtain', label: 'Rèm trượt', sfx: 'swipe' },
  { id: 'blur', label: 'Nhòe nhanh', sfx: 'whoosh' },
  { id: 'stripes', label: 'Sọc chéo thương hiệu', sfx: 'riser' },
  { id: 'cube', label: 'Xoay khối lập phương 3D', sfx: 'swirl' },
  { id: 'flip', label: 'Lật thẻ 3D', sfx: 'swipe' },
] as const;
export type VideoTransitionId = (typeof VIDEO_TRANSITIONS)[number]['id'];
export const VIDEO_TRANSITION_IDS = VIDEO_TRANSITIONS.map((s) => s.id) as [VideoTransitionId, ...VideoTransitionId[]];
/** auto = xoay vòng theo bộ của mẫu video; hoặc một kiểu cho mọi lần chuyển cảnh */
export type TransitionChoice = 'auto' | VideoTransitionId;

export const VIDEO_TEMPLATES = [
  {
    id: 'funny-site',
    label: 'Tình huống hài công trình',
    desc: 'Thợ gặp chuyện dở khóc dở cười, Minh Tiến giải cứu',
    tone: 'fun',
    music: 'bounce',
    intro: 'bounce',
    outro: 'call-now',
    transitions: ['whip', 'shake', 'slide-up', 'glitch'],
    brief:
      'Mẫu "Tình huống hài công trình": cảnh 1 một sự cố nhỏ dở khóc dở cười ở công trình (thiếu hàng, hàng sai quy cách, chờ xe mãi không tới) quay như clip đời thường, biểu cảm thật; giữa video Minh Tiến xuất hiện giải quyết nhanh gọn (có hàng sẵn, giao tận nơi bằng xe cẩu); kết bằng câu chốt hài, thân thiện. Hài nhẹ nhàng, không chê bai khách, không mạo hiểm an toàn lao động.',
  },
  {
    id: 'before-after',
    label: 'Trước – sau',
    desc: 'Đặt hai cảnh đối lập: chưa dùng / đã dùng Minh Tiến',
    tone: 'friendly',
    music: 'corporate',
    intro: '3d-logo',
    outro: 'cards',
    transitions: ['curtain', 'iris', 'flash', 'zoom'],
    brief:
      'Mẫu "Trước – sau": nửa đầu là "trước" (vấn đề thật: xưởng nóng, đường ống rỉ, hệ thống chưa đạt), nửa sau là "sau" khi dùng sản phẩm / giải pháp Minh Tiến, cùng một góc máy để thấy khác biệt. Ghi rõ chữ "TRƯỚC" / "SAU" trong mô tả hình. Không bịa số liệu hiệu quả nếu dữ liệu không có.',
  },
  {
    id: 'day-in-life',
    label: '1 ngày làm việc (POV)',
    desc: 'Theo chân NV kho, tài xế xe cẩu hoặc NV kinh doanh',
    tone: 'friendly',
    music: 'lofi',
    intro: 'light-sweep',
    outro: 'classic',
    transitions: ['slide-up', 'whip', 'dissolve', 'zoom'],
    brief:
      'Mẫu "1 ngày làm việc" góc nhìn thứ nhất (POV): mở bằng giờ giấc ("6:30 sáng ở kho Minh Tiến"), các cảnh ngắn nhịp nhanh theo chân một nhân viên (soạn hàng, cẩu hàng lên xe, giao tới công trình, khách nhận hàng). Chân thực, hậu trường thật, kết bằng lời mời liên hệ.',
  },
  {
    id: 'test-proof',
    label: 'Thử thách kiểm chứng',
    desc: 'Đo, cân, thử áp, so mác thép ngay trước ống kính',
    tone: 'professional',
    music: 'industry',
    intro: 'sparks',
    outro: 'split',
    transitions: ['zoom', 'shake', 'flash', 'stripes'],
    brief:
      'Mẫu "Thử thách kiểm chứng": cảnh 1 đặt câu hỏi gây tò mò ("Ống này dày đúng 2,0 ly không?"), các cảnh sau kiểm chứng bằng dụng cụ thật (thước kẹp, cân, đồng hồ áp, CO/CQ) quay cận cảnh, kết luận rõ ràng. Chỉ nêu thông số có trong dữ liệu sản phẩm, không bịa kết quả đo.',
  },
  {
    id: 'top3',
    label: 'Top 3 lỗi thường gặp',
    desc: 'Danh sách ngắn kiểu "3 sai lầm khi chọn…"',
    tone: 'professional',
    music: 'funk',
    intro: 'countdown',
    outro: 'cards',
    transitions: ['slide-up', 'stripes', 'whip', 'iris'],
    brief:
      'Mẫu "Top 3": tiêu đề kiểu "3 sai lầm khi chọn …" hoặc "3 điều kỹ sư cần biết về …", mỗi cảnh một ý đánh số 1-2-3 (ghi số trong mô tả hình), mỗi ý 1 câu ngắn dễ nhớ, cảnh cuối là cách Minh Tiến giúp tránh lỗi. Chỉ nêu kiến thức đúng chuẩn, không dọa khách.',
  },
  {
    id: 'qna',
    label: 'Khách hỏi – Minh Tiến đáp',
    desc: 'Một câu hỏi thật của khách, trả lời gọn trong 30 giây',
    tone: 'friendly',
    music: 'piano',
    intro: 'blueprint',
    outro: 'call-now',
    transitions: ['dissolve', 'zoom', 'iris', 'slide-up'],
    brief:
      'Mẫu "Khách hỏi – Minh Tiến đáp": cảnh 1 hiện câu hỏi thường gặp của khách (như tin nhắn Zalo hoặc khách hỏi trực tiếp), các cảnh sau nhân viên Minh Tiến trả lời ngắn gọn, minh họa bằng hàng thật ở kho, kết bằng "Còn thắc mắc? Nhắn Zalo Minh Tiến".',
  },
  {
    id: 'delivery',
    label: 'Giao hàng tận nơi / mở hàng',
    desc: 'Xe cẩu Minh Tiến chở hàng tới, khách mở kiểm hàng',
    tone: 'friendly',
    music: 'edm',
    intro: 'pipes',
    outro: 'ticker',
    transitions: ['whip', 'cube', 'shake', 'flash'],
    brief:
      'Mẫu "Giao hàng tận nơi": nhịp nhanh như clip mở hộp, xe cẩu Minh Tiến lăn bánh, cẩu hàng xuống công trình, khách kiểm hàng (nhãn mác, CO/CQ, quy cách), cái gật đầu hài lòng. Nhấn mạnh giao nhanh, đúng hàng; không bịa thời gian giao nếu dữ liệu không có.',
  },
  {
    id: 'trend-beat',
    label: 'Bắt trend: cắt theo nhịp',
    desc: 'Cảnh rất ngắn, cắt đúng nhịp nhạc, chữ to',
    tone: 'fun',
    music: 'phonk',
    intro: '3d-text',
    outro: 'neon',
    transitions: ['glitch', 'cube', 'flash', 'spin', 'flip'],
    brief:
      'Mẫu "Bắt trend cắt theo nhịp": nhiều cảnh rất ngắn (1,5–3 giây) cắt đúng nhịp nhạc, mỗi cảnh một hình mạnh (tia lửa cắt thép, ống xếp thẳng tắp, xe cẩu nâng hàng), lời rất ít và chữ to, câu mở 2 giây đầu phải gây sốc thị giác. Phong cách TikTok trẻ trung nhưng vẫn là doanh nghiệp B2B.',
  },
  {
    id: 'fire-alert',
    label: 'Báo động cháy – giải pháp PCCC',
    desc: 'Mở bằng tình huống nguy cơ cháy, chốt bằng thiết bị PCCC đạt chuẩn',
    tone: 'professional',
    music: 'epic',
    intro: 'siren',
    outro: 'split',
    transitions: ['flash', 'shake', 'zoom', 'stripes'],
    brief:
      'Mẫu "Báo động cháy": cảnh 1 tín hiệu nguy cơ (chuông báo cháy reo, khói ở kho hàng, bình chữa cháy hết hạn) dựng bằng hình an toàn, không quay cháy thật, không gây hoảng loạn; các cảnh sau là thiết bị PCCC Minh Tiến (bình, đầu phun, ống, tủ báo cháy) và cách chúng bảo vệ công trình; kết bằng lời khuyên kiểm tra định kỳ + liên hệ tư vấn. Chỉ nêu quy chuẩn, thông số có trong dữ liệu, không hứa "đạt nghiệm thu" nếu không có căn cứ.',
  },
  {
    id: 'quick-promo',
    label: 'Khuyến mãi nhanh 15 giây',
    desc: 'Báo tin hàng mới về / ưu đãi đang có, chốt gọi ngay',
    tone: 'fun',
    music: 'sale',
    intro: 'countdown',
    outro: 'call-now',
    transitions: ['flash', 'stripes', 'zoom', 'shake'],
    brief:
      'Mẫu "Khuyến mãi nhanh": 15–20 giây, 3 cảnh: tin nóng (hàng mới về / còn sẵn số lượng lớn), hàng thật ở kho, lời kêu gọi gọi ngay. CHỈ nêu ưu đãi, giá, số lượng nếu có trong yêu cầu của nhân viên hoặc dữ liệu; không có thì nói "hàng sẵn kho, báo giá nhanh".',
  },
] as const satisfies readonly {
  id: string;
  label: string;
  desc: string;
  tone: 'friendly' | 'professional' | 'fun';
  music: MusicStyleId;
  intro: VideoIntroId;
  outro: VideoOutroId;
  transitions: readonly VideoTransitionId[];
  brief: string;
}[];
export type VideoTemplateId = (typeof VIDEO_TEMPLATES)[number]['id'];
export const VIDEO_TEMPLATE_IDS = VIDEO_TEMPLATES.map((s) => s.id) as [VideoTemplateId, ...VideoTemplateId[]];
export const videoTemplate = (id: string | null | undefined) => VIDEO_TEMPLATES.find((t) => t.id === id) ?? null;

/** Bộ chuyển cảnh mặc định khi chưa chọn mẫu (giữ đúng 4 kiểu DEC-516) */
export const DEFAULT_TRANSITIONS: readonly VideoTransitionId[] = ['whip', 'zoom', 'dissolve', 'flash'];
/** Kiểu chuyển cảnh ở ranh giới thứ `boundary`: một kiểu cố định, hoặc xoay vòng bộ của mẫu */
export function transitionAt(boundary: number, choice: TransitionChoice, pack: readonly VideoTransitionId[] = DEFAULT_TRANSITIONS): VideoTransitionId {
  if (choice !== 'auto') return choice;
  const list = pack.length ? pack : DEFAULT_TRANSITIONS;
  return list[((boundary % list.length) + list.length) % list.length]!;
}

/** Thẻ của file ghi âm dùng làm nhạc nền công ty (Tài liệu doanh nghiệp → File ghi âm, video) */
export const COMPANY_MUSIC_TAG = 'nhạc nền';

/**
 * Meme viral (anh Tiến 26/10/2026: "meme mẫu thường dùng… viral", "lên ý tưởng làm meme"). Ảnh meme nổi tiếng (ảnh phim, người thật) dính bản quyền
 * và quyền hình ảnh nên không tải về: trình duyệt tự vẽ khung meme + tự tổng hợp âm thanh meme. `text` mặc định; "A|B" = 2 vế (Kỳ vọng|Thực tế, Khách|Minh Tiến).
 */
export const VIDEO_MEMES = [
  { id: 'vine-boom', label: 'Bùm! (zoom giật)', sec: 1.2, text: 'BÙM!', hint: 'Con số gây sốc, kết quả thử nghiệm' },
  { id: 'record-scratch', label: 'Cào đĩa – dừng hình', sec: 2.6, text: 'Chắc bạn đang thắc mắc…', hint: 'Mở đầu kiểu kể lại' },
  { id: 'pov', label: 'POV:', sec: 3, text: 'POV: công trình cần ống gấp lúc 9 giờ tối', hint: 'Đặt người xem vào vai khách' },
  { id: 'expect-reality', label: 'Kỳ vọng / Thực tế', sec: 3, text: 'Hàng trôi nổi|Hàng có CO/CQ', hint: 'So sánh 2 vế' },
  { id: 'chat', label: 'Khách hỏi – Minh Tiến đáp', sec: 3.5, text: 'Còn hàng không shop?|Có sẵn, giao trong ngày!', hint: 'Tin nhắn hỏi đáp' },
  { id: 'plot-twist', label: 'Plot twist', sec: 1.8, text: 'PLOT TWIST', hint: 'Cú lật bất ngờ' },
  { id: 'alo', label: 'Ủa alo?', sec: 1.6, text: 'Ủa alo?', hint: 'Phản ứng ngạc nhiên với cái sai' },
  { id: 'emoji-rain', label: 'Mưa emoji', sec: 2.2, text: '😂🔥💯', hint: 'Ăn mừng, vui' },
  { id: 'wait', label: 'Đợi đến cuối…', sec: 3, text: 'Đợi đến cuối…', hint: 'Giữ người xem tới cuối' },
  { id: 'dinh-noc', label: 'Đỉnh nóc kịch trần', sec: 2, text: 'Đỉnh nóc, kịch trần!', hint: 'Khen giá / chất lượng' },
  { id: 'toang', label: 'Toang! (kèn buồn)', sec: 2.4, text: 'Toang!', hint: 'Hậu quả của hàng kém' },
  { id: 'correct', label: '✔ Chuẩn (ting)', sec: 1.4, text: 'Chuẩn!', hint: 'Làm đúng' },
  { id: 'wrong', label: '✘ Sai (còi)', sec: 1.4, text: 'Sai rồi!', hint: 'Làm sai' },
  { id: 'mind-blown', label: 'Sốc toàn tập', sec: 1.8, text: 'Không thể tin nổi!', hint: 'Ưu đãi / tốc độ bất ngờ' },
  { id: 'follow', label: 'Nút Theo dõi', sec: 2, text: 'Theo dõi Minh Tiến', hint: 'Kêu gọi theo dõi cuối video' },
] as const satisfies readonly { id: string; label: string; sec: number; text: string; hint: string }[];
export type VideoMemeId = (typeof VIDEO_MEMES)[number]['id'];
export const VIDEO_MEME_IDS = VIDEO_MEMES.map((m) => m.id) as [VideoMemeId, ...VideoMemeId[]];
export const videoMeme = (id: string | null | undefined) => VIDEO_MEMES.find((m) => m.id === id) ?? null;

/** Kho ý tưởng meme viết sẵn cho ngành (vai cảnh: hook mở đầu, problem vấn đề, proof bằng chứng, cta kêu gọi) */
export const MEME_IDEAS = [
  { id: 'thep-re', topic: 'thep', role: 'problem', meme: 'toang', text: 'Ham thép rẻ 2 nghìn/kg… 1 năm sau: Toang!' },
  { id: 'thep-coco', topic: 'thep', role: 'proof', meme: 'expect-reality', text: 'Thép không giấy tờ|Thép đủ CO/CQ, đúng mác' },
  { id: 'thep-can', topic: 'thep', role: 'proof', meme: 'vine-boom', text: 'Cân lại: đủ từng kg' },
  { id: 'thep-cat', topic: 'thep', role: 'hook', meme: 'mind-blown', text: 'Cắt theo bản vẽ, không tính phí!' },
  { id: 'thep-gap', topic: 'thep', role: 'hook', meme: 'chat', text: '9 giờ tối còn giao không shop?|Xe cẩu lên đường liền!' },
  { id: 'thep-kho', topic: 'thep', role: 'hook', meme: 'plot-twist', text: 'Plot twist: kho có sẵn hàng trăm tấn' },
  { id: 'pccc-hethan', topic: 'pccc', role: 'problem', meme: 'alo', text: 'Ủa alo? Bình chữa cháy hết hạn 2 năm rồi?' },
  { id: 'pccc-dauphun', topic: 'pccc', role: 'problem', meme: 'wrong', text: 'Đầu phun lắp sai khoảng cách' },
  { id: 'pccc-qcvn', topic: 'pccc', role: 'proof', meme: 'correct', text: 'Đúng QCVN, nghiệm thu một lần đạt' },
  { id: 'pccc-pov', topic: 'pccc', role: 'hook', meme: 'pov', text: 'POV: mai nghiệm thu PCCC mà còn thiếu van' },
  { id: 'pccc-thuap', topic: 'pccc', role: 'proof', meme: 'vine-boom', text: 'Thử áp 16 bar: không rỉ' },
  { id: 'pccc-nghiemthu', topic: 'pccc', role: 'proof', meme: 'emoji-rain', text: '🎉✅🔥' },
  { id: 'chung-thacmac', topic: 'chung', role: 'hook', meme: 'record-scratch', text: 'Chắc bạn đang thắc mắc vì sao nhà thầu chọn bên mình…' },
  { id: 'chung-cuoi', topic: 'chung', role: 'hook', meme: 'wait', text: 'Đợi đến cuối xem kết quả thử' },
  { id: 'chung-gia', topic: 'chung', role: 'cta', meme: 'dinh-noc', text: 'Giá này còn giao tận chân công trình?' },
  { id: 'chung-follow', topic: 'chung', role: 'cta', meme: 'follow', text: 'Theo dõi Minh Tiến' },
] as const satisfies readonly { id: string; topic: 'thep' | 'pccc' | 'chung'; role: 'hook' | 'problem' | 'proof' | 'cta'; meme: VideoMemeId; text: string }[];
export const MEME_TOPIC_LABEL = { thep: 'Sắt thép', pccc: 'PCCC', chung: 'Chung' } as const;
